import React, { useEffect, useState, useRef, useMemo, memo } from 'react';
import { Button, Col, Progress, Row } from 'reactstrap';
import styles from 'styles/Chat.module.scss';
import PropTypes from 'prop-types';

import { RecordState } from 'constants/constants';

import CountdownTimer from './CountdownTimer';
import CameraOptions from './CameraOptions';
import RecordingControls from './RecordingControls';
import GroupButtonSubmit from '../CustomButton/GroupButtonSubmit';
import { showToastError } from '../../@core/components/toastify/ToastTypes';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { getColor, getColorText } from 'store/settings';
import { X } from 'react-feather';
import { useTest, useUnit } from 'hooks/units';

const getAudioContext = () =>
  (typeof window !== undefined && window.AudioContext) ||
  window.webkitAudioContext;

const MEDIA_RECORDER_TIMESLICE_MS = 2000;

const VideoRecord = ({
  onSubmit,
  onCancel,
  titleSubmit,
  isLoading,
  type,
  progressOrder,
}) => {
  // const [errorMessage, setErrorMessage] = useState('');
  // const [file, setFile] = useState(null);
  // const [startRecordTime, setStartRecordTime] = useState(null);
  const unitSelected = useUnit();
  const test = useTest();

  const noOfVideoFile =
    unitSelected?.noOfVideoFile ||
    Number(test?.question?.config?.video?.noOfVideo);

  const maxVideoSize =
    unitSelected?.maxVideoSize ||
    Number(test?.question?.config?.video?.maxVideoSize);

  const [recordState, setRecordState] = useState(RecordState.IDLE);
  const [isRequestingMedia, setIsRequestingMedia] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [haveDeviceAccess, setHaveDeviceAccess] = useState(false);
  const [isMicDeviceEnabled, setIsMicDeviceEnabled] = useState(false);
  const [files, setFiles] = useState([]);
  const [videoDeviceId, setVideoDeviceId] = useState('');
  const [audioDeviceId, setAudioDeviceId] = useState('');
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const audioInterval = useRef(null);
  const mediaChunks = useRef([]);
  const finalBlob = useRef(null);
  const countdownTimerRef = useRef(null);
  const [deviceList, setDevices] = useState({ video: [], audio: [] });

  const videoSource = 'camera';
  const colorStatus = useSelector(getColor);
  const colorTextStatus = useSelector(getColorText);

  const Stylescal = useMemo(
    () => styled.div`
      .btn-danger {
        border-color: ${colorStatus} !important;
        background-color: ${colorStatus} !important;
        color: ${colorTextStatus} !important;
      }
      .btn-danger:hover {
        border-color: ${colorStatus} !important;
        background-color: #ffffff !important;
        color: ${colorStatus} !important;
      }
    `,
    [],
  );
  const removeFile = (index) => {
    const arrFiles = [...files];
    arrFiles.splice(index, 1);
    setFiles(arrFiles);
  };

  useEffect(() => {
    //
    // This updates the device list when the list changes. For example
    // plugging in or unplugging a mic or camera
    //
    navigator.mediaDevices.ondevicechange = getDevices;

    return () => stopUserMedia();
  }, []);

  useEffect(() => {
    if (isMicDeviceEnabled || videoDeviceId || audioDeviceId) {
      startAv();
    }
  }, [videoDeviceId, audioDeviceId, isMicDeviceEnabled]);

  const prepRecording = () => {
    if (typeof MediaRecorder === 'undefined') {
      showToastError(
        'MediaRecorder is not available in your browser. Consider enabling it in Experimental Features.',
      );
      return;
    }
    if (files.length >= noOfVideoFile) {
      showToastError(
        `You can upload a maximum of ${noOfVideoFile} video. Please remove extra video`,
      );
      return null;
    }
    countdownTimerRef.current?.start();
    setRecordState(RecordState.PREPARING);
  };

  const getDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const list = { video: [], audio: [] };

    devices.forEach((device) => {
      if (device.kind === 'videoinput') {
        list.video.push(device);
      }
      if (device.kind === 'audioinput') {
        list.audio.push(device);
      }
    });
    setDevices({ ...list });
  };

  const updateAudioLevels = (analyser) => {
    // dataArray will give us an array of numbers ranging from 0 to 255
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
    // cthese values are between 0 - 255, we want the average and
    // to convert it into a value between 0 - 100
    const audioLevelValue = Math.round((average / 255) * 100);
    setAudioLevel(audioLevelValue);
  };

  const stopUserMedia = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        console.error('stopping track', track.kind, track.label);
        track.stop();
      });
    }
    streamRef.current = null;
  };

  /*
   * Stop all recording, cancel audio interval
   */
  const cleanup = () => {
    console.error('cleanup');
    if (recorderRef.current) {
      if (recorderRef?.current?.state === 'inactive') {
        console.error('skipping recorder stop() b/c state is "inactive"');
      } else {
        recorderRef.current.onstop = function onRecorderStop() {
          console.error('recorder cleanup');
        };
        recorderRef.current.stop();
      }
    }
    mediaChunks.current = [];
    if (audioInterval.current) {
      clearInterval(audioInterval.current);
    }
    setRecordState(RecordState.IDLE);
  };

  const cancelRecording = () => {
    countdownTimerRef.current?.reset();
    cleanup();
  };

  const stopRecording = () => {
    if (!recorderRef.current) {
      console.warn('cannot stopRecording() without a recorderRef');
      return;
    }
    recorderRef.current.onstop = async function onRecorderStop() {
      finalBlob.current = new Blob(mediaChunks.current, {
        type: 'video/ogg; codecs=opus',
      });

      // Check the file size

      let fileSizeInBytes = finalBlob.current.size;
      let fileSizeInMB = (fileSizeInBytes / (1024 * 1024)).toFixed(2);
      if (fileSizeInMB > maxVideoSize) {
        showToastError(
          `Video exceeds ${maxVideoSize} MB. Please upload a smaller video`,
        );
        reset();
        return null;
      }
      // Create a File object from the Blob and set the name
      let fileName = `video-from-camera${Date.now().toString()}.ogg`;
      let file = new File([finalBlob.current], fileName, {
        type: 'video/ogg; codecs=opus',
      });

      // Add the File object to the array
      let newFiles = [...files];
      newFiles.push(file);
      setFiles(newFiles);

      const objUrl = URL.createObjectURL(finalBlob.current);

      if (videoRef.current !== null) {
        videoRef.current.srcObject = null;
        videoRef.current.src = objUrl;
        videoRef.current.controls = true;
        videoRef.current.muted = false;
        setIsReviewing(true);
      }
      cleanup();
    };
    recorderRef.current.stop();
    stopUserMedia();
  };

  const submitRecording = () => {
    if (!finalBlob.current) {
      console.error('Cannot submit recording without a blob');
      return;
    }
    const createdFile = new File([finalBlob.current], 'video-from-camera.ogg', {
      type: finalBlob.current.type,
    });
    // setFile(createdFile);
    onSubmit(files);
  };

  /*
   * do a cleanup, and also cancel all media streams
   */
  const hardCleanup = () => {
    cleanup();
    stopUserMedia();
    setIsReviewing(false);
    setIsLoadingPreview(false);
    setHaveDeviceAccess(false);
    setIsMicDeviceEnabled(false);
    setVideoDeviceId('');
    setAudioDeviceId('');
  };

  const reset = async () => {
    cleanup();
    stopUserMedia();
    setIsReviewing(false);
    setIsLoadingPreview(false);
    await startCamera();
  };

  const startAv = () => {
    cleanup();
    if (!videoSource) {
      console.error('Cannot startAv without a video source');
      return;
    }
    if (videoSource === 'camera') {
      startCamera();
    }
  };

  const setupStream = (stream) => {
    const AudioContext = getAudioContext();

    if (
      (AudioContext && videoSource === 'camera') ||
      (AudioContext && videoSource === 'screen' && isMicDeviceEnabled)
    ) {
      const audioContext = new AudioContext();
      const mediaStreamSource = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.smoothingTimeConstant = 0.3;
      analyser.fftSize = 1024;
      mediaStreamSource.connect(analyser);

      audioInterval.current = window.setInterval(() => {
        updateAudioLevels(analyser);
      }, 100);
    }
    streamRef.current = stream;
    if (videoRef.current !== null) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.controls = false;
    }
    setHaveDeviceAccess(true);
  };

  const startCamera = async () => {
    if (navigator.mediaDevices) {
      const video = videoDeviceId ? { deviceId: videoDeviceId } : true;
      const audio = audioDeviceId ? { deviceId: audioDeviceId } : true;
      const constraints = { video, audio };
      try {
        /*
         * We have to call getDevices() twice b/c of firefox.
         * The first time we getDevices() in firefox the device.label is an empty string
         * After getUserMedia() happens successfully, then we can getDevices() again to
         * and the 2nd time then device.label will be populated :shrug:
         */
        await getDevices();
        console.error('requesting user media with constraints', constraints);
        /*
         * This gets called when a new device is selected, we want to stopUserMedia()
         * when re-initializing a camera
         *
         * You will notice that in startScreenshare() we do not call stopUserMedia()
         * because we want the screenshare to stay the same while the microphone input
         * gets changed
         */
        stopUserMedia();
        setIsRequestingMedia(true);
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        setIsRequestingMedia(false);
        await getDevices();
        setupStream(stream);
      } catch (err) {
        console.error('getdevices error', err);
        setIsRequestingMedia(false);
        showToastError(
          'Error getting devices, you may have denied access already, if so you will have to allow access in browser settings.',
        );
      }
    } else {
      showToastError('navigator.mediaDevices not available in this browser');
    }
    return function teardown() {
      hardCleanup();
    };
  };

  /*
   * When recording your own camera it is really disorienting if
   * the video is not the mirror image.
   */
  const showMirrorImage = () => {
    if (videoSource === 'camera') {
      return !isReviewing;
    }
    return false;
  };

  const selectVideo = (value) => {
    setVideoDeviceId(value);
  };

  const selectAudio = (value) => {
    setAudioDeviceId(value);
  };

  const muteAudioTrack = (shouldMute) => {
    if (streamRef.current) {
      streamRef.current
        .getTracks()
        .filter((track) => track.kind === 'audio')
        .forEach((track) => {
          track.enabled = !shouldMute;
        });
    }
  };

  const isRecording = recordState === 'RECORDING';

  const startRecording = async () => {
    if (isRecording) {
      console.warn('we are already recording');
      return;
    }
    if (isReviewing) {
      console.warn(
        'cannot start recording when you are reviewing your last recording',
      );
      return;
    }
    try {
      // setStartRecordTime(new Date().valueOf());
      // const preferredOptions = { mimeType: 'video/webm;codecs=vp9' };
      // const backupOptions = { mimeType: 'video/webm;codecs=vp8,opus' };
      // let options = preferredOptions;
      // /*
      //  * MediaRecorder.isTypeSupported is not a thing in safari,
      //  * good thing safari supports the preferredOptions
      //  */
      // if (typeof MediaRecorder.isTypeSupported === 'function') {
      //   if (!MediaRecorder.isTypeSupported(preferredOptions.mimeType)) {
      //     options = backupOptions;
      //   }
      // }

      const stream = streamRef.current;
      if (!stream) throw new Error('Cannot record without a stream');
      // recorderRef.current = new MediaRecorder(stream, options);
      recorderRef.current = new MediaRecorder(stream);
      recorderRef.current.start(MEDIA_RECORDER_TIMESLICE_MS);
      recorderRef.current.ondataavailable = (evt) => {
        mediaChunks.current.push(evt.data);
      };
      setRecordState(RecordState.RECORDING);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
      // setErrorMessage(
      //   'Error attempting to start recording, check console for details',
      // );
    }
  };

  const isMuted = () => {
    if (streamRef.current) {
      return !!streamRef.current
        .getTracks()
        .filter((track) => track.kind === 'audio' && !track.enabled).length;
    }
    return false;
  };

  useEffect(() => {
    if (type) {
      setFiles([]);
    }
  }, [type]);

  const handleCancel = async () => {
    await hardCleanup();
    onCancel();
  };

  return (
    <div>
      <div>
        <div className="skeleton-container">
          {!haveDeviceAccess && videoSource === 'camera' && (
            <Button.Ripple onClick={startCamera}>
              {isRequestingMedia
                ? 'Loading device...'
                : 'Allow the browser to use your camera/mic'}
            </Button.Ripple>
          )}
        </div>
        <div className="video-container">
          <video
            className={showMirrorImage() ? 'mirror-image' : ''}
            ref={videoRef}
            width="500"
            autoPlay
          />
          <CountdownTimer ref={countdownTimerRef} onElapsed={startRecording} />
        </div>
        <div>{isLoadingPreview && 'Loading preview...'}</div>
        {haveDeviceAccess && videoSource === 'camera' && (
          <CameraOptions
            isLoadingPreview={isLoadingPreview}
            isRecording={isRecording}
            isMuted={isMuted()}
            muteAudioTrack={muteAudioTrack}
            deviceList={deviceList}
            audioLevel={audioLevel}
            selectVideo={selectVideo}
            selectAudio={selectAudio}
            videoDeviceId={videoDeviceId}
          />
        )}
        {haveDeviceAccess && (
          <RecordingControls
            recordState={recordState}
            isLoadingPreview={isLoadingPreview}
            isReviewing={isReviewing}
            startRecording={prepRecording}
            cancelRecording={cancelRecording}
            stopRecording={stopRecording}
            submitRecording={submitRecording}
            reset={reset}
            titleSubmit={titleSubmit}
          />
        )}
        <div className="centerDiv">
          <GroupButtonSubmit
            onCancel={handleCancel}
            disabled={isLoadingPreview || isLoading}
          >
            <Stylescal>
              <Button.Ripple
                color="danger"
                className="round"
                onClick={submitRecording}
                disabled={isLoadingPreview || isLoading || !finalBlob?.current}
              >
                {isLoadingPreview ? 'Loading preview...' : titleSubmit}
              </Button.Ripple>
            </Stylescal>
          </GroupButtonSubmit>
        </div>
      </div>
      <style jsx>
        {`
          .error-message {
            color: #c9473f;
            max-width: 400px;
            padding-bottom: 20px;
            text-align: center;
            line-height: 24px;
          }

          .skeleton-container {
            width: 100%;
            display: flex;
            justify-content: center;
          }

          .video-container {
            position: relative;
            display: flex;
            justify-content: center;
          }

          video {
            display: ${haveDeviceAccess ? 'block' : 'none'};
            border-radius: 30px;
          }

          video.mirror-image {
            -webkit-transform: scaleX(-1);
            transform: scaleX(-1);
          }

          h1 {
            margin-top: 3vh;
            font-size: 3.5vw;
          }

          .centerDiv {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 20px;
          }
        `}
      </style>

      {files.length ? (
        <div className="my-1">
          <p>Your video:</p>
          {files.map((item, index) => (
            <Row className="mx-0" key={index}>
              <Col className="col-12 mb-05">
                {item?.name}
                <Button.Ripple
                  className="btn-icon rounded-circle ml-05 px-0 py-0"
                  outline
                  color="secondary"
                  onClick={() => removeFile(index)}
                >
                  <X size={16} />
                </Button.Ripple>
                {progressOrder === index ? (
                  <span
                    className={`pl-05 font-style-italic text-secondary font-small-2 `}
                  >
                    Uploading...
                  </span>
                ) : null}
              </Col>
              <Col className="col-12">
                <div className="mb-50">
                  {progressOrder === index ? (
                    <Progress
                      striped
                      id={`progress-upload-file-${index}`}
                      className={styles['progress-upload-message']}
                    />
                  ) : null}
                </div>
              </Col>
            </Row>
          ))}
        </div>
      ) : null}
    </div>
  );
};

VideoRecord.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  titleSubmit: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

export default memo(VideoRecord);
